import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/****************
 * THE FIRST SWAP COSTS WHAT THE SECOND ONE COSTS. GB-3.
 *
 * Guillaume, 2026-09-22: "it's not just the best trade selector, it's almost
 * everything that require a change of data display. The Best Trader have the
 * same issue when I change the period, the Trader's page when I go from All to
 * Live, every time we need to swap for displaying A to display B, the first
 * swap is slow, than swapping between them is okay."
 *
 * That is a cache doing exactly what it was told. Each choice is its own query
 * key, so the first click on Month fetches Month, and every later click is
 * instant because it is already held. Nothing is broken; the data was simply
 * never asked for until somebody wanted to look at it.
 *
 * So ask for it early. Once the chosen view has settled, quietly fetch the
 * others, and by the time anyone clicks they are already in the cache.
 *
 * ── Why it is safe to be greedy here ────────────────────────────────────────
 *
 * These are two or three small reads on endpoints the page already calls with
 * a different argument, and prefetchQuery is a no-op on a key that is already
 * fresh -- so a page left open does not re-fetch them on a timer, and nothing
 * here can evict or overwrite the view being looked at.
 *
 * ── And why it waits ────────────────────────────────────────────────────────
 *
 * `enabled` is how a caller says "the thing the user actually asked for has
 * arrived". Firing at mount would put the alternatives in the same network
 * queue as the view on screen, which makes the page the user IS looking at
 * slower in order to make one they might never open faster. requestIdleCallback
 * takes it one step further and waits for the browser to be doing nothing,
 * falling back to a short timer on Safari, which does not have it.
 ****************/

export interface PrefetchChoice {
  queryKey: unknown[];
  queryFn: () => Promise<unknown>;
  /** How long the prefetched answer counts as fresh. Match the real hook. */
  staleTime?: number;
}

export function usePrefetchChoices(
  choices: PrefetchChoice[],
  /** Fetch them only once the view the user asked for has arrived. */
  enabled: boolean,
): void {
  const queryClient = useQueryClient();

  // The keys, as a string, so an array rebuilt on every render does not
  // restart the effect on every render.
  const signature = JSON.stringify(choices.map((c) => c.queryKey));

  useEffect(() => {
    if (!enabled || choices.length === 0) return;

    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      for (const choice of choices) {
        void queryClient.prefetchQuery({
          queryKey: choice.queryKey,
          queryFn: choice.queryFn,
          staleTime: choice.staleTime ?? 15_000,
        });
      }
    };

    // requestIdleCallback is absent on Safari and in older WebKit, where the
    // timer is the whole of the behaviour rather than a degradation: a second
    // is long enough that the visible view is done.
    const idle = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (typeof idle === 'function') {
      const handle = idle(run, { timeout: 2000 });
      return () => {
        cancelled = true;
        const cancelIdle = (window as unknown as {
          cancelIdleCallback?: (h: number) => void;
        }).cancelIdleCallback;
        cancelIdle?.(handle);
      };
    }
    const timer = window.setTimeout(run, 1000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // choices is rebuilt every render by every caller; signature is what
    // actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, signature, queryClient]);
}
